import { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useLocales } from 'src/locales';
import { titleCase } from 'src/utils/change-case';
import Iconify from 'src/components/iconify';
import CustomTooltip from './CustomTooltip';
import SerpModalButton from './SerpModalButton';
import { PlaItem } from '../../types';

interface Props {
    items: PlaItem[];
    layout?: 'carousel' | 'grid';
    showBrandChip?: boolean;
}

export default function PlaCarousel({ items, layout = 'carousel', showBrandChip = false }: Props) {
    const displayItems = useMemo(() => items.slice(0, layout === 'grid' ? 60 : 60), [items, layout]);
    const { t } = useLocales();
    return (
        <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">{titleCase(t("dashboard.global.visibility.top_product_listing_ads"))}</Typography>
                </Stack>
                <Typography variant="caption" fontSize={14} color="text.secondary">{items.length} {t("dashboard.global.visibility.results_found")}</Typography>
            </Stack>
            {items.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.neutral' }}>
                    <Typography variant="body2" color="text.secondary">No PLA ads found for this country</Typography>
                </Paper>
            ) : (
                <Box sx={layout === 'grid' ? {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: 1.5,
                } : {
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    gap: 1.5,
                    pb: 1,
                    pr: 1,
                    scrollbarWidth: 'thin',
                    msOverflowStyle: 'auto',
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                }}>
                    {displayItems.map((ad, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 0, backgroundColor: 'background.paper', ...(layout === 'carousel' ? { minWidth: 250, maxWidth: 250 } : {}) }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    height: 100,
                                    borderRadius: '10px 10px 0 0',
                                    bgcolor: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    mb: 1,
                                }}
                            >
                                <Box sx={{
                                    position: 'absolute',
                                    height: 16,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '16px',
                                    top: 6,
                                    right: 6,
                                    zIndex: 1,
                                    backgroundColor: 'action.hover',
                                    px: 0.5
                                }}>
                                    <CustomTooltip title={`Search term: ${ad.searchTerm}`} placement="top">
                                        <Iconify icon="mdi:information-outline" width={14} height={14} sx={{ color: 'text.secondary' }} />
                                    </CustomTooltip>
                                </Box>
                                {ad.img ? (
                                    <Box component="img" src={ad.img} alt={ad.title} sx={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'white', transform: 'scale(1.4)', transformOrigin: 'center center' }} />
                                ) : (
                                    <Iconify icon="mdi:image-outline" />
                                )}
                            </Box>
                            <Stack spacing={0.5} p={0}>
                                <Box p={1}>
                                    <Box sx={{
                                        position: 'relative',
                                        ...(showBrandChip && (ad as any).brandInfringementTitle && {
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                width: '120px',
                                                height: '16px',
                                                background: 'linear-gradient(90deg, transparent, background.paper 40%)',
                                                pointerEvents: 'none'
                                            }
                                        })
                                    }}>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontSize: 12,
                                                lineClamp: 3, 
                                                display: '-webkit-box', 
                                                WebkitLineClamp: 3, 
                                                WebkitBoxOrient: 'vertical', 
                                                overflow: 'hidden', 
                                                displayPrint: 'block',
                                                minHeight: '58px'
                                            }}
                                        >
                                            {ad.title}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{ad.price}</Typography>
                                        {showBrandChip && ((ad as any).brandInfringementTitle || (ad as any).brandInfringementDescription) && (
                                            <Chip label="Brand" color="error" size="small" sx={{ height: 16 }} />
                                        )}
                                    </Stack>
                                </Box>
                                <Box sx={{ backgroundColor: 'background.neutral', borderRadius: '10px', borderTop: '1px solid divider' }}>
                                    <Box p={1}>
                                        <Stack direction="column" alignItems="space-between" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                            <Stack direction="row" spacing={1} justifyContent="space-between">
                                                <Typography variant="caption" sx={{ opacity: 0.8, color: 'text.secondary' }}>Position</Typography>
                                                <Typography variant="caption" fontWeight={500} color="text.primary">{ad.position}</Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={1} justifyContent="space-between">
                                                <Typography variant="caption" sx={{ opacity: 0.8, color: 'text.secondary' }}>Frequency</Typography>
                                                <Typography variant="caption" fontWeight={500} color="text.primary">{ad.frequency}%</Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                    <Paper sx={{ p: 0, backgroundColor: 'action.hover', borderRadius: '0 0 4px 4px' }}>
                                        <Stack direction="row" spacing={1} p={1} justifyContent="space-between">
                                            <Typography variant="caption" sx={{ opacity: 0.8, color: 'text.secondary' }}>SERP</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CustomTooltip title={`Search term: ${ad.searchTerm}`} placement="top">
                                                    <SerpModalButton
                                                        gcsUrl={(ad as any)._gcs_uri_mobile}
                                                        deviceType={ad.device_type}
                                                        iconType="mobile"
                                                        searchTerm={ad.searchTerm}
                                                    />
                                                </CustomTooltip>
                                                <CustomTooltip title={`Search term: ${ad.searchTerm}`} placement="top">
                                                    <SerpModalButton
                                                        gcsUrl={(ad as any)._gcs_uri_desktop}
                                                        deviceType={ad.device_type}
                                                        iconType="desktop"
                                                        searchTerm={ad.searchTerm}
                                                    />
                                                </CustomTooltip>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                </Box>
                            </Stack>
                        </Paper>
                    ))}
                </Box>
            )}
        </Stack>
    );
}


